import * as Ariakit from '@ariakit/react'
import clsx from '@proton/utils/clsx'
import { type ReactNode, forwardRef } from 'react'
import { Icon } from './icon'

// dropdown
// --------

export const DropdownPopover = forwardRef<HTMLDivElement, Ariakit.RoleProps>(function DropdownPopover(props, ref) {
  return (
    <Ariakit.Role
      ref={ref}
      {...props}
      className={clsx(
        'border-weak z-10 max-h-[--popover-available-height] max-w-[--popover-available-width] overflow-auto overscroll-contain rounded-[.5rem] border bg-[--background-norm] py-2 text-[--text-norm] shadow-[0px_8px_24px_0px_rgba(0,0,0,0.16)] focus:outline-none',
        props.className,
      )}
    />
  )
})

export const DROPDOWN_POPOVER_DEFAULTS = {
  // TODO: is portal really a good default here?
  portal: true, // TODO: Ariakit might already default to this and the JSDoc of Menu/Select might be wrong
  gutter: 4,
}

const DROPDOWN_ITEM_SELECTED_INDICATOR_ICON = 'checkmark'
const DROPDOWN_ITEM_SUBMENU_INDICATOR_ICON = 'chevron-right-filled'
export type DropdownItemOptions = {
  /**
   * Expected to be 16px wide.
   */
  leadingIconSlot?: ReactNode
  /**
   * If `true`, the leading icon slot will default to the selected indicator icon. Overrides `leadingIndent`.
   */
  selectedIndicator?: boolean
  /**
   * If `true`, the leading icon slot will default to empty space resulting in a similar indentation as other
   * items with icons. This is helpful when only some of the items have icons and you want to keep the
   * alignment consistent.
   */
  leadingIndent?: boolean
  /**
   * A slot for trailing content, such as keyboard shortcut hints. Has a default text color.
   */
  hintSlot?: ReactNode
  /**
   * Expected to be 16px wide.
   */
  trailingIconSlot?: ReactNode
  /**
   * If `true`, the trailing icon slot will default to the submenu indicator icon.
   */
  submenuIndicator?: boolean
  /**
   * Set to `false` to disable the default padding.
   * @default true
   */
  padding?: boolean
}
export interface DropdownItemProps extends Ariakit.RoleProps, DropdownItemOptions {}
export const DropdownItem = forwardRef<HTMLDivElement, DropdownItemProps>(function DropdownItem(
  {
    leadingIconSlot,
    selectedIndicator,
    leadingIndent,
    hintSlot,
    trailingIconSlot,
    submenuIndicator,
    padding = true,
    ...props
  },
  ref,
) {
  leadingIconSlot =
    leadingIconSlot ??
    (selectedIndicator ? (
      <Icon legacyName={DROPDOWN_ITEM_SELECTED_INDICATOR_ICON} className="text-[--text-norm]" />
    ) : undefined) ??
    (leadingIndent ? <span className="w-4" /> : undefined)

  if (submenuIndicator) {
    trailingIconSlot = trailingIconSlot ?? (
      <Icon legacyName={DROPDOWN_ITEM_SUBMENU_INDICATOR_ICON} className="text-current" />
    )
  }
  return (
    <Ariakit.Role
      ref={ref}
      {...props}
      className={clsx(
        'flex select-none flex-nowrap items-start gap-2 py-[.4375rem] text-[.875rem] text-[--text-norm]',
        'disabled:text-[--text-hint] aria-disabled:text-[--text-hint]',
        padding && 'px-4',
        // TODO: "hocus" type tw variant
        'hover:bg-[--interaction-weak-minor-2] focus:outline-none focus-visible:bg-[--interaction-weak-minor-2] data-[active-item]:bg-[--interaction-weak-minor-2] data-[focus-visible]:bg-[--interaction-weak-minor-2]',
        'aria-expanded:bg-[--interaction-weak-minor-2]',
        '[[data-theme-mode=dark]_&]:hover:!bg-[--interaction-weak-minor-1] [[data-theme-mode=dark]_&]:focus-visible:!bg-[--interaction-weak-minor-1] [[data-theme-mode=dark]_&]:aria-expanded:!bg-[--interaction-weak-minor-1] [[data-theme-mode=dark]_&]:data-[active-item]:!bg-[--interaction-weak-minor-1] [[data-theme-mode=dark]_&]:data-[focus-visible]:!bg-[--interaction-weak-minor-1]',
        // TODO: see hack for specificity, otherwise active styles are overridden by data-[focus-visible] :(
        'active:active:bg-[--interaction-weak-minor-1] data-[active]:bg-[--interaction-weak-minor-1]',
        '[[data-theme-mode=dark]_&]:active:!bg-[--interaction-weak] [[data-theme-mode=dark]_&]:data-[active]:!bg-[--interaction-weak]',
        props.className,
      )}
    >
      {leadingIconSlot && <span className="flex h-[1.375rem] shrink-0 items-center">{leadingIconSlot}</span>}
      <span className="grow leading-[1.375rem]">
        {hintSlot ? (
          <span className="flex grow justify-between gap-4">
            <span className="grow truncate">{props.children}</span>
            <span className="truncate text-[--text-weak]">{hintSlot}</span>
          </span>
        ) : (
          props.children
        )}
      </span>

      {trailingIconSlot && <span className="flex h-[1.375rem] shrink-0 items-center">{trailingIconSlot}</span>}
    </Ariakit.Role>
  )
})

// tooltip
// -------

export type TooltipOptions = {
  /**
   * A slot for trailing content, such as keyboard shortcut hints.
   */
  trailingSlot?: ReactNode
}
export interface TooltipProps extends Ariakit.RoleProps, TooltipOptions {}
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip({ trailingSlot, ...props }, ref) {
  return (
    <Ariakit.Role
      ref={ref}
      {...props}
      className={clsx(
        'leading-0 z-20 flex shrink-0 items-center gap-1 rounded-[.5rem] bg-[#0C0C14] px-2 py-[.375rem] text-[.75rem] text-[white] shadow-[0px_-2px_12px_0px_rgba(0,0,0,0.05)]',
        props.className,
      )}
    >
      <span className="grow leading-none">{props.children}</span>
      {trailingSlot && <span className="flex shrink-0 items-center">{trailingSlot}</span>}
    </Ariakit.Role>
  )
})
